import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const getAudienceNamespace = () => {
  const audience = config.auth0Audience || config.apiUrl;
  try {
    return new URL(audience).origin;
  } catch {
    return audience;
  }
};

const AUTH0_AUDIENCE = config.auth0Audience || config.apiUrl;
const AUTH0_AUDIENCE_NAMESPACE = getAudienceNamespace();
const IMPERSONATE_STORAGE_KEY = 'impersonateUser';
const IMPERSONATE_HEADER = 'X-Impersonate-User-Id';

const readStoredImpersonation = () => {
  try {
    const raw = sessionStorage.getItem(IMPERSONATE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const isSafeHttpMethod = (method) => {
  const normalised = (method || 'GET').toUpperCase();
  return normalised === 'GET' || normalised === 'HEAD' || normalised === 'OPTIONS';
};

const isSyncUserUrl = (url) => typeof url === 'string' && url.includes('/api/auth/sync-user');

const notifyImpersonationBlocked = () => {
  window.dispatchEvent(new CustomEvent('impersonation-blocked', {
    detail: { message: "You're viewing as another user — actions are disabled." }
  }));
};

// Helper function to extract roles from token
const extractRolesFromToken = (token) => {
  if (!token) return null;
  
  try {
    const decoded = jwtDecode(token);
    
    // Check common locations where Auth0 might store roles
    const roleClaims = [
      decoded[`${AUTH0_AUDIENCE_NAMESPACE}/roles`],
      decoded[`${AUTH0_AUDIENCE_NAMESPACE}/role`],
      decoded['http://localhost:3000/roles'],
      decoded['http://localhost:3000/role'],
      decoded.roles,
      decoded.role,
      // Check app_metadata for roles (common Auth0 pattern)
      decoded.app_metadata?.roles,
      decoded.app_metadata?.role,
      // Check user_metadata
      decoded.user_metadata?.roles,
      decoded.user_metadata?.role,
      // Check permissions array for role-like permissions
      decoded.permissions,
      decoded.scope?.split(' ').find(s => s.includes('role')),
    ];
    
    // Return the first non-null role claim
    for (const claim of roleClaims) {
      if (claim) {
        // If it's an array, return the first role
        if (Array.isArray(claim)) {
          // If the array is not empty, return the first role
          if (claim.length > 0) {
            return claim[0];
          }
        }
        // If it's a string, return it directly
        else if (typeof claim === 'string') {
          return claim;
        }
        // If it's an object with a name property (some Auth0 configs)
        else if (typeof claim === 'object' && claim.name) {
          return claim.name;
        }
      }
    }
    
    // No roles found
    return null;
  } catch (error) {
    console.error('Error extracting roles from token:', error);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dbUser, setDbUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [auth0Role, setAuth0Role] = useState(null);
  const [impersonatedUser, setImpersonatedUser] = useState(readStoredImpersonation);
  
  // Use Auth0 hooks
  const {
    isAuthenticated,
    user: auth0User,
    getAccessTokenSilently,
    loginWithRedirect,
    logout: auth0Logout,
    isLoading: auth0Loading
  } = useAuth0();

  // State to track if we need to force a login due to token issues
  const [shouldForceLogin, setShouldForceLogin] = useState(false);

  // Set up axios with the Auth0 token
  useEffect(() => {
    const getToken = async () => {
      if (isAuthenticated && auth0User && !shouldForceLogin) {
        try {
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: AUTH0_AUDIENCE,
              scope: 'openid profile email read:roles'
            },
            detailedResponse: true
          });
          
          // Store the token for role extraction
          setAccessToken(token.access_token);
          
          // Try to extract role from token
          const roleFromToken = extractRolesFromToken(token.access_token);
          setAuth0Role(roleFromToken);
          
          // Set the token for all axios requests
          axios.defaults.headers.common['Authorization'] = `Bearer ${token.access_token}`;
          setLoading(false);
        } catch (err) {
          console.error('Error getting token:', err);
          
          // Check if this is a refresh token error
          if (err.message && err.message.includes('Missing Refresh Token')) {
            setShouldForceLogin(true);
            setError('Session expired. Please log in again.');
          } else {
            setError('Authentication error. Please log in again.');
          }
          setLoading(false);
        }
      } else if (!auth0Loading) {
        delete axios.defaults.headers.common['Authorization'];
        delete axios.defaults.headers.common[IMPERSONATE_HEADER];
        sessionStorage.removeItem(IMPERSONATE_STORAGE_KEY);
        setImpersonatedUser(null);
        setDbUser(null);
        setAccessToken(null);
        setAuth0Role(null);
        setLoading(false);
      }
    };

    getToken();
  }, [isAuthenticated, auth0User, getAccessTokenSilently, auth0Loading, shouldForceLogin]);

  // Attach impersonation header to axios and fetch; block writes while viewing as another user
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((config) => {
      const stored = readStoredImpersonation();
      if (!stored?.id) return config;
      if (isSyncUserUrl(config.url)) return config;

      if (!isSafeHttpMethod(config.method || 'get')) {
        notifyImpersonationBlocked();
        return Promise.reject(new Error('VIEW_ONLY_IMPERSONATION'));
      }

      config.headers = config.headers || {};
      config.headers[IMPERSONATE_HEADER] = String(stored.id);
      return config;
    });

    const originalFetch = window.fetch.bind(window);
    window.fetch = (input, init = {}) => {
      const stored = readStoredImpersonation();
      if (!stored?.id) {
        return originalFetch(input, init);
      }

      const url = typeof input === 'string' ? input : input?.url;
      const method = init.method || (typeof input !== 'string' ? input.method : null) || 'GET';

      if (isSyncUserUrl(url)) {
        return originalFetch(input, init);
      }

      if (!isSafeHttpMethod(method)) {
        notifyImpersonationBlocked();
        return Promise.reject(new Error('VIEW_ONLY_IMPERSONATION'));
      }

      if (typeof input === 'string') {
        const headers = new Headers(init.headers || {});
        headers.set(IMPERSONATE_HEADER, String(stored.id));
        return originalFetch(input, { ...init, headers });
      }

      const headers = new Headers(input.headers);
      headers.set(IMPERSONATE_HEADER, String(stored.id));
      return originalFetch(new Request(input, { ...init, headers }));
    };

    const stored = readStoredImpersonation();
    if (stored?.id) {
      axios.defaults.headers.common[IMPERSONATE_HEADER] = String(stored.id);
    }

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      window.fetch = originalFetch;
    };
  }, []);

  // Sync user data with our database when authenticated
  useEffect(() => {
    const syncUserWithDatabase = async () => {
      if (isAuthenticated && auth0User && !auth0Loading && !shouldForceLogin) {
        try {
          
          // Make sure we have a token set for axios
          let token = null;
          try {
            const tokenResponse = await getAccessTokenSilently({
              authorizationParams: {
                audience: AUTH0_AUDIENCE,
                scope: 'openid profile email read:roles'
              },
              detailedResponse: true
            });
            token = tokenResponse.access_token;
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          } catch (tokenError) {
            console.warn('Could not get token for sync:', tokenError);
            
            // Check if this is a refresh token error
            if (tokenError.message && tokenError.message.includes('Missing Refresh Token')) {
              setShouldForceLogin(true);
              return; // Exit early, don't try to sync
            }
            // Continue without token for other errors - server will handle it
          }
          
          const response = await axios.post(`${config.apiUrl}/api/auth/sync-user`, {
            auth0Id: auth0User.sub,
            email: auth0User.email,
            name: auth0User.name || auth0User.nickname,
            picture: auth0User.picture
          });
          
          if (response.data && response.data.user) {
            setDbUser(response.data.user);
            
            // If the server returned an Auth0 role, use it
            if (response.data.user.auth0Role) {
              setAuth0Role(response.data.user.auth0Role);
            }
          } else {
            console.error('Invalid response from sync-user endpoint:', response.data);
            setError('Error syncing user data: Invalid server response');
          }
        } catch (error) {
          console.error('Error syncing user with database:', error);
          console.error('Error details:', error.response?.data || 'No response data');
          
          // More user-friendly error handling
          let errorMessage = 'Unknown error syncing user data';
          if (error.response) {
            if (error.response.status === 401) {
              errorMessage = 'Authentication error. Please try logging out and back in.';
            } else if (error.response.status === 500) {
              errorMessage = 'Server error syncing user data. Please try again later.';
            } else {
              errorMessage = `Error syncing user data: ${error.response?.data?.error || error.message}`;
            }
          }
          setError(errorMessage);
          
          // Wait and try again after a short delay with a more basic approach
          setTimeout(async () => {
            try {
              
              // Save the current token before removing it from headers
              const currentToken = axios.defaults.headers.common['Authorization'] || null;
              
              // Retry without setting token headers to work around potential token issues
              delete axios.defaults.headers.common['Authorization'];
              
              const retryResponse = await axios.post(`${config.apiUrl}/api/auth/sync-user`, {
                auth0Id: auth0User.sub,
                email: auth0User.email,
                name: auth0User.name || auth0User.nickname,
                picture: auth0User.picture
              });
              
              if (retryResponse.data && retryResponse.data.user) {
                setDbUser(retryResponse.data.user);
                setError(null);
                
                // If the server returned an Auth0 role, use it
                if (retryResponse.data.user.auth0Role) {
                  setAuth0Role(retryResponse.data.user.auth0Role);
                }
                
                // Restore the token for future requests
                if (currentToken) {
                  axios.defaults.headers.common['Authorization'] = currentToken;
                }
              }
            } catch (retryError) {
              console.error('Retry also failed:', retryError);
              // If the retry also fails, try one more time to set up the user locally
              if (!dbUser && auth0User) {
                // Create a minimal local user to allow the app to function
                setDbUser({
                  id: 0,
                  name: auth0User.name || auth0User.nickname || auth0User.email,
                  email: auth0User.email,
                  role: 'user',
                  auth0_id: auth0User.sub,
                  picture: auth0User.picture
                });
                setError('Limited functionality - database sync failed');
              }
            }
          }, 1000);
        }
      }
    };

    syncUserWithDatabase();
  }, [isAuthenticated, auth0User, auth0Loading, shouldForceLogin]);

  // Login function - using Auth0's loginWithRedirect
  const login = async () => {
    setError(null);
    setShouldForceLogin(false);
    try {
      const returnTo = localStorage.getItem('returnTo') || '/app';
      await loginWithRedirect({
        appState: { returnTo }
      });
      return true;
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
      return false;
    }
  };

  // Logout function - using Auth0's logout
  const logout = () => {
    sessionStorage.removeItem(IMPERSONATE_STORAGE_KEY);
    setImpersonatedUser(null);
    delete axios.defaults.headers.common[IMPERSONATE_HEADER];
    setDbUser(null);
    setAccessToken(null);
    setAuth0Role(null);
    setShouldForceLogin(false); // Reset the force login flag
    auth0Logout({ 
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  const startImpersonation = (target) => {
    if (!target?.id || (target.role || '').toLowerCase() === 'admin') return;
    const payload = {
      id: target.id,
      name: target.name,
      email: target.email,
      role: target.role,
      viewAllRecords: target.viewAllRecords
    };
    sessionStorage.setItem(IMPERSONATE_STORAGE_KEY, JSON.stringify(payload));
    axios.defaults.headers.common[IMPERSONATE_HEADER] = String(target.id);
    setImpersonatedUser(payload);
  };

  const stopImpersonation = () => {
    sessionStorage.removeItem(IMPERSONATE_STORAGE_KEY);
    delete axios.defaults.headers.common[IMPERSONATE_HEADER];
    setImpersonatedUser(null);
  };

  // Determine role precedence (Database role has priority over Auth0 role)
  const getEffectiveRole = () => {
    if (impersonatedUser?.role) {
      return impersonatedUser.role.toLowerCase();
    }
    if (dbUser?.role) {
      // Database role takes precedence - this is our source of truth
      return dbUser.role.toLowerCase();
    } else if (auth0Role) {
      // Fall back to Auth0 role only if no database role
      return auth0Role.toLowerCase();
    }
    return 'sales'; // Default to sales instead of 'user'
  };

  // Role convenience checks
  const role = getEffectiveRole();
  
  // Debug role selection
  useEffect(() => {
    if (dbUser || auth0Role) {
    }
  }, [dbUser?.role, auth0Role, role]);
  

  
  const isAdmin = role === 'admin';
  const isManager = role === 'manager' || isAdmin;
  const isSales = role === 'sales' || isManager;
  const isDesigner = role === 'designer';

  // Get the combined user object with both Auth0 and database data
  const user = dbUser ? {
    ...dbUser,
    ...(impersonatedUser || {}),
    auth0: auth0User,
    effectiveRole: role,
    auth0Role: auth0Role
  } : null;

  // Wrapper function for getToken that always includes the correct parameters
  const getToken = async (options = {}) => {
    try {
      const tokenResponse = await getAccessTokenSilently({
        authorizationParams: {
          audience: AUTH0_AUDIENCE,
          scope: 'openid profile email read:roles',
          ...options.authorizationParams
        },
        ...options
      });
      
      // If detailedResponse was requested, return the full response
      // Otherwise, return just the access token string
      if (options.detailedResponse) {
        return tokenResponse;
      }
      
      // Handle both string responses and object responses
      return typeof tokenResponse === 'string' ? tokenResponse : tokenResponse.access_token || tokenResponse;
    } catch (error) {
      console.error('Error getting access token:', error);
      throw error;
    }
  };

  // Value to be provided by context
  const value = {
    user,
    loading: loading || auth0Loading,
    error,
    isAuthenticated: isAuthenticated && !!dbUser && !shouldForceLogin, // Only consider authenticated if we have both Auth0 and DB user and no force login needed
    login,
    logout,
    setError,
    getToken, // Use our wrapper function instead of getAccessTokenSilently directly
    token: accessToken,
    // Role information
    role,
    auth0Role,
    isAdmin,
    isManager,
    isSales,
    isDesigner,
    shouldForceLogin, // Expose this for components that might need it
    isImpersonating: !!impersonatedUser,
    impersonator: impersonatedUser ? dbUser : null,
    startImpersonation,
    stopImpersonation
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
